import { readResponse } from '@web/api/read'
import type {
  AppBinding,
  AppDefinition,
  AppLaunch,
  BridgeConfig,
  DemoScenarios,
  GitHubConnection,
  GitHubRepository,
  GitHubRepositoryPage,
  GitInfo,
  IdentityState,
  LoginProvider,
  OnePagerLaunchShare,
  PermissionMode,
  ProjectListing,
  RelatedSession,
  RepositoryCheckout,
  SessionArtifact,
  SessionConfig,
  SessionCostHistory,
  SessionPage,
  SessionRecord,
  UserPreferences,
} from '@web/api/types'

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''
const AUTH_ENTRY_TIMEOUT_MS = 15_000

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const send = !init?.method || init.method === 'GET' ? readResponse : fetch
  const res = await send(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    if (
      (res.status === 401 || res.status === 403) &&
      !path.startsWith('/v1/auth/') &&
      path !== '/v1/access'
    ) {
      window.dispatchEvent(new Event('bridge:identity-expired'))
    }
    // HTTP/2 responses can have an empty statusText, including gateway failures.
    // Always retain a visible error when the proxy returns HTML instead of JSON.
    let detail = `Canopy could not complete the request (HTTP ${res.status}). Please try again.`
    try {
      const body = (await res.json()) as { detail?: unknown }
      if (body.detail)
        detail =
          typeof body.detail === 'string'
            ? body.detail
            : JSON.stringify(body.detail)
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, detail)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

async function boundedAuthRequest<T>(
  path: string,
  init: RequestInit,
): Promise<T> {
  const controller = new AbortController()
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Canopy did not respond in time. Please try again.'))
      controller.abort()
    }, AUTH_ENTRY_TIMEOUT_MS)
  })
  try {
    return await Promise.race([
      request<T>(path, { ...init, signal: controller.signal }),
      timeout,
    ])
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId)
  }
}

export const api = {
  sessionShares: (id: string) =>
    request<{ recipient_id: string; name: string; email: string }[]>(
      `/v1/sessions/${encodeURIComponent(id)}/shares`,
    ),
  shareSession: (id: string, email: string) =>
    request(`/v1/sessions/${encodeURIComponent(id)}/shares`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  revokeSessionShare: (id: string, recipient: string) =>
    request(
      `/v1/sessions/${encodeURIComponent(id)}/shares/${encodeURIComponent(recipient)}`,
      { method: 'DELETE' },
    ),
  identity: () => boundedAuthRequest<IdentityState>('/v1/auth/state', {}),
  savePreferences: (preferences: PermissionMode | UserPreferences) =>
    request<IdentityState>('/v1/auth/preferences', {
      method: 'PATCH',
      body: JSON.stringify(
        typeof preferences === 'string'
          ? { default_permission_mode: preferences }
          : preferences,
      ),
    }),
  discoverLogin: (email: string) =>
    boundedAuthRequest<LoginProvider>('/v1/auth/discover', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  startSso: (email: string) =>
    boundedAuthRequest<{ authorization_url: string }>('/v1/auth/sso/start', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  login: (email: string, password: string) =>
    request<IdentityState>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  setIdentity: (context: { user_id?: string; org_id?: string }) =>
    request<IdentityState>('/v1/auth/context', {
      method: 'POST',
      body: JSON.stringify(context),
    }),
  logout: (options: { user_id?: string; all?: boolean } = {}) =>
    request<IdentityState>('/v1/auth/logout', {
      method: 'POST',
      body: JSON.stringify(options),
    }),
  unlock: (token: string) =>
    request<void>('/v1/access', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  apps: () =>
    request<AppDefinition[]>('/v1/apps/catalog').then((catalog) => {
      const descriptors = new Map<
        string,
        NonNullable<AppDefinition['descriptor']>
      >()
      for (const app of catalog) {
        if (app.descriptor) descriptors.set(app.id, app.descriptor)
      }
      return catalog.map((app) => {
        if (!app.descriptorRef) return app
        const { descriptorRef, ...definition } = app
        const descriptor = descriptors.get(descriptorRef)
        return descriptor ? { ...definition, descriptor } : definition
      })
    }),
  onepagerLaunchShares: () =>
    request<{ shares: OnePagerLaunchShare[] }>('/v1/apps/onepager/shares'),
  openApp: (appId: string) =>
    request<AppLaunch>(`/v1/apps/${encodeURIComponent(appId)}/open`, {
      method: 'POST',
    }),
  launchApp: (
    appId: string,
    options: {
      requestId?: string
      packageDigest?: string
      project?: string
      environment?: string
      draft?: boolean
      background?: boolean
      prompt?: string
      implicit?: boolean
      config?: Partial<SessionConfig>
    } = {},
  ) =>
    request<AppLaunch>(`/v1/apps/${encodeURIComponent(appId)}/launch`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),
  appBinding: (bindingId: string) =>
    request<AppBinding>(`/v1/apps/bindings/${encodeURIComponent(bindingId)}`),
  connectApp: (bindingId: string, config: Partial<SessionConfig>) =>
    request<SessionRecord>(
      `/v1/apps/bindings/${encodeURIComponent(bindingId)}/session`,
      { method: 'POST', body: JSON.stringify({ config }) },
    ),
  config: () => request<BridgeConfig>('/v1/config'),
  projects: (path = '', environment = 'local') =>
    request<ProjectListing>(
      `/v1/projects?path=${encodeURIComponent(path)}&environment=${encodeURIComponent(environment)}`,
    ),
  gitInfo: (path = '', environment = 'local') =>
    request<GitInfo>(
      `/v1/projects/git?path=${encodeURIComponent(path)}&environment=${encodeURIComponent(environment)}`,
    ),
  clone: (url: string, dest: string | null, environment = 'local') =>
    request<{ path: string; url: string }>(
      `/v1/projects/clone?environment=${encodeURIComponent(environment)}`,
      {
        method: 'POST',
        body: JSON.stringify({ url, dest }),
      },
    ),
  sessions: (appId?: string, archived = false, before?: string) => {
    const query = new URLSearchParams()
    if (appId) query.set('app_id', appId)
    if (archived) query.set('archived', 'true')
    if (before) query.set('before', before)
    query.set('summary', 'true')
    return request<SessionPage>(`/v1/sessions/page?${query}`)
  },
  session: (id: string) => request<SessionRecord>(`/v1/sessions/${id}`),
  sessionApp: (id: string) =>
    request<AppDefinition>(`/v1/sessions/${encodeURIComponent(id)}/app`),
  appScenarios: (appId: string, packageDigest?: string | null) =>
    request<DemoScenarios>(
      `/v1/apps/${encodeURIComponent(appId)}/scenarios${packageDigest ? `?packageDigest=${encodeURIComponent(packageDigest)}` : ''}`,
    ),
  changeScenario: (id: string, scenarioId: string) =>
    request<SessionRecord>(`/v1/sessions/${encodeURIComponent(id)}/scenario`, {
      method: 'PATCH',
      body: JSON.stringify({ scenario_id: scenarioId }),
    }),
  sessionArtifacts: (id: string) =>
    request<{ artifacts: SessionArtifact[] }>(
      `/v1/sessions/${encodeURIComponent(id)}/artifacts`,
    ),
  sessionUploads: (id: string) =>
    request<{ uploads: SessionArtifact[] }>(
      `/v1/sessions/${encodeURIComponent(id)}/uploads`,
    ),
  uploadSessionFile: (
    sessionId: string,
    file: { path: string; mime_type: string; content_base64: string },
    idempotencyKey: string,
  ) =>
    request<SessionArtifact>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/uploads`,
      {
        method: 'POST',
        body: JSON.stringify({
          title: file.path.slice(0, 200),
          idempotency_key: idempotencyKey,
          files: [file],
        }),
      },
    ),
  sessionArtifact: (sessionId: string, artifactId: string) =>
    request<SessionArtifact>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}`,
    ),
  sessionHandoffs: (id: string) =>
    request<{ outgoing: RelatedSession[]; incoming: RelatedSession[] }>(
      `/v1/sessions/${encodeURIComponent(id)}/handoffs`,
    ),
  sessionChildren: (id: string) =>
    request<{
      children: RelatedSession[]
      parent_session?: RelatedSession | null
    }>(`/v1/sessions/${encodeURIComponent(id)}/children`),
  sessionCosts: (id: string, after = 0, limit = 100) =>
    request<SessionCostHistory>(
      `/v1/sessions/${encodeURIComponent(id)}/costs?after=${after}&limit=${limit}`,
    ),
  createSession: (config: Partial<SessionConfig>, prompt?: string) =>
    request<SessionRecord>('/v1/sessions', {
      method: 'POST',
      body: JSON.stringify({
        config,
        prompt: prompt ?? null,
        background: true,
      }),
    }),
  renameSession: (id: string, title: string) =>
    request<SessionRecord>(`/v1/sessions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),
  /** The profile's GitHub connection state; never the token. */
  githubConnection: (userId: string) =>
    request<GitHubConnection>(
      `/v1/connections/${encodeURIComponent(userId)}/github`,
    ),
  /** Repositories the connected GitHub account can clone, newest activity first. */
  githubRepositories: (
    userId: string,
    options: { query?: string; page?: number; perPage?: number } = {},
  ) => {
    const params = new URLSearchParams()
    if (options.query) params.set('query', options.query)
    if (options.page) params.set('page', String(options.page))
    if (options.perPage) params.set('per_page', String(options.perPage))
    const search = params.toString()
    return request<GitHubRepositoryPage>(
      `/v1/connections/${encodeURIComponent(userId)}/github/repositories${search ? `?${search}` : ''}`,
    )
  },
  /** Create an initialized repository under the connected GitHub account. */
  createGithubRepository: (
    userId: string,
    body: { name: string; description?: string; private?: boolean },
  ) =>
    request<GitHubRepository>(
      `/v1/connections/${encodeURIComponent(userId)}/github/repositories`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  generateSessionTitle: (id: string, requestId: string, messages: string[]) =>
    request<SessionRecord>(`/v1/sessions/${encodeURIComponent(id)}/title`, {
      method: 'POST',
      body: JSON.stringify({ request_id: requestId, messages }),
    }),
  /** Save the browser's startup timeline in the session journal. */
  reportClientEvents: (
    id: string,
    payload: {
      kind: 'startup' | 'resume' | 'interaction'
      launch_unix_ms: number
      events: { name: string; at_ms: number }[]
    },
  ) =>
    request<{ ok: boolean }>(
      `/v1/sessions/${encodeURIComponent(id)}/client_events`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  resumeSession: (id: string) =>
    request<SessionRecord>(`/v1/sessions/${id}/resume`, { method: 'POST' }),
  startSession: (id: string) =>
    request<SessionRecord>(`/v1/sessions/${id}/start`, { method: 'POST' }),
  configureApp: async (
    bindingId: string,
    action: string,
    values: Record<string, unknown>,
  ) => {
    const root = `/v1/apps/bindings/${encodeURIComponent(bindingId)}`
    const state = await request<{ revision: number }>(`${root}/state`)
    return request(`${root}/actions/${encodeURIComponent(action)}`, {
      method: 'POST',
      body: JSON.stringify({
        input: values,
        expectedRevision: state.revision,
        idempotencyKey: crypto.randomUUID(),
      }),
    })
  },
  mcpConnections: () =>
    request<{
      connections: { id: string; title: string; connected: boolean }[]
    }>('/v1/mcp-connections'),
  saveMcpConnection: (server: string, token: string) =>
    request(`/v1/mcp-connections/${encodeURIComponent(server)}`, {
      method: 'PUT',
      body: JSON.stringify({ token }),
    }),
  removeMcpConnection: (server: string) =>
    request(`/v1/mcp-connections/${encodeURIComponent(server)}`, {
      method: 'DELETE',
    }),
  attachRepository: (id: string, repository: RepositoryCheckout) =>
    request<SessionRecord>(
      `/v1/sessions/${encodeURIComponent(id)}/repositories`,
      { method: 'POST', body: JSON.stringify(repository) },
    ),
  updateConfig: (id: string, config: Partial<SessionConfig>, restart = false) =>
    request<SessionRecord>(`/v1/sessions/${id}/config`, {
      method: 'PATCH',
      body: JSON.stringify({ config, restart }),
    }),
  sleepSession: (id: string) =>
    request<SessionRecord>(`/v1/sessions/${id}/sleep`, { method: 'POST' }),
  command: (id: string, command: Record<string, unknown>) =>
    request<unknown>(`/v1/sessions/${id}/command`, {
      method: 'POST',
      body: JSON.stringify(command),
    }),
  stopSession: (id: string) =>
    request<SessionRecord>(`/v1/sessions/${id}/stop`, { method: 'POST' }),
  archiveSession: (id: string) =>
    request<{ ok: boolean }>(`/v1/sessions/${id}/archive`, { method: 'POST' }),
}

/** File URLs are assembled from opaque identifiers, never generated paths or URLs. */
export function artifactFileUrl(
  sessionId: string,
  artifactId: string,
  fileId: string,
  download = false,
  kind: 'artifacts' | 'uploads' = 'artifacts',
): string {
  return `${BASE}/v1/sessions/${encodeURIComponent(sessionId)}/${kind}/${encodeURIComponent(artifactId)}/files/${encodeURIComponent(fileId)}${download ? '?download=true' : ''}`
}

export function wsUrl(path: string): string {
  const base = BASE || window.location.origin
  const url = new URL(path, base)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}
